<template>
  <div @mousedown="recordActivity">
    <div
      v-if="$root.userLoggedIn && $root.showInactivityAlert"
      class="inactivity-alert">
      You will be logged out soon. Please click anywhere to extend your session.
    </div>

    <transition name="fade">
      <component
        :is="layout"
        v-if="fetched">
        <transition name="switch">
          <router-view />
        </transition>
      </component>
    </transition>

    <template v-if="$root.userLoggedIn">
      <airport-popup />
      <airport-handler-popup />
      <checklist-item-popup />
      <checklist-items-popup />
      <checklist-template-popup />
      <checklist-templates-popup />
      <document-download-popup />
      <document-popup />
      <flight-warnings-popup />
      <handler-popup />
      <handler-phone-popup />
      <import-pax-items-popup />
      <licence-popup />
      <note-popup />
      <permission-popup />
      <reward-popup />
      <trip-handling-request-popup />
      <visa-popup />
      <warning-popup />
      <monitoring-popup />
      <div
        v-if="$root.airportsLoadInProgress"
        class="airports-list-overlay">
        <span>Loading airports list&hellip;</span>
      </div>
    </template>
  </div>
</template>

<script>
import AirportPopup from './components/common/AirportPopup.vue';
import AirportHandlerPopup from './components/common/AirportHandlerPopup.vue';
import ChecklistItemPopup from './components/common/ChecklistItemPopup.vue';
import ChecklistItemsPopup from './components/common/ChecklistItemsPopup.vue';
import ChecklistTemplatePopup from './components/common/ChecklistTemplatePopup.vue';
import ChecklistTemplatesPopup from './components/common/ChecklistTemplatesPopup.vue';
import DocumentPopup from './components/common/DocumentPopup.vue';
import DocumentDownloadPopup from './components/common/DocumentDownloadPopup.vue';
import FlightWarningsPopup from './components/common/FlightWarningsPopup.vue';
import FormUtils from './utils/FormUtils.js';
import HandlerPopup from './components/common/HandlerPopup.vue';
import HandlerPhonePopup from './components/common/HandlerPhonePopup.vue';
import ImportPaxItemsPopup from './components/common/ImportPaxItemsPopup.vue';
import LicencePopup from './components/common/LicencePopup.vue';
import NotePopup from './components/common/NotePopup.vue';
import PermissionPopup from './components/common/PermissionPopup.vue';
import RewardPopup from './components/common/RewardPopup.vue';
import SanitizeUtils from './utils/SanitizeUtils';
import TripHandlingRequestPopup from './components/common/TripHandlingRequestPopup.vue';
import VisaPopup from './components/common/VisaPopup.vue';
import Vue from 'vue';
import MonitoringPopup from './components/common/MonitoringPopup.vue';
import WarningPopup from './components/common/WarningPopup.vue';

export default {
  components: {
    'airport-popup': AirportPopup,
    'airport-handler-popup': AirportHandlerPopup,
    'checklist-item-popup': ChecklistItemPopup,
    'checklist-items-popup': ChecklistItemsPopup,
    'checklist-template-popup': ChecklistTemplatePopup,
    'checklist-templates-popup': ChecklistTemplatesPopup,
    'document-popup': DocumentPopup,
    'document-download-popup': DocumentDownloadPopup,
    'flight-warnings-popup': FlightWarningsPopup,
    'handler-popup': HandlerPopup,
    'handler-phone-popup': HandlerPhonePopup,
    'import-pax-items-popup': ImportPaxItemsPopup,
    'licence-popup': LicencePopup,
    'monitoring-popup': MonitoringPopup,
    'note-popup': NotePopup,
    'permission-popup': PermissionPopup,
    'reward-popup': RewardPopup,
    'trip-handling-request-popup': TripHandlingRequestPopup,
    'visa-popup': VisaPopup,
    'warning-popup': WarningPopup
  },
  data () {
    return {
      fetched: false
    }
  },
  computed: {
    layout () {
      if (!this.$route.meta) {
        return 'default-layout';
      }

      return `${this.$route.meta.layout || 'default'}-layout`;
    },
  },
  mounted () {
    this.fetched = true;
    window.crmApp = true;
    window.crmRefreshCalendar = () => {
      this.$bus.$emit('refresh-calendar');
    };
    window.crmGetRedirect = () => {
      let urlRedirect = sessionStorage.getItem('login-redirect')

      if (urlRedirect && urlRedirect !== 'null' && urlRedirect.indexOf('/dashboard/monitoring/view/') > -1) {
        let id = urlRedirect.replace('/dashboard/monitoring/view/', '');
        urlRedirect = '/dashboard/monitoring/view/' + parseInt(id, 10);
      }

      if (urlRedirect === 'null' || !!urlRedirect) {
        return false;
      }

      sessionStorage.removeItem('login-redirect');
      return urlRedirect;
    };
    this.autoRefreshToken();
    this.loadGoogleMapsAPI();
    this.recordActivity();
    this.checkActivity();
  },
  methods: {
    refreshToken () {
      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/auth/generate-token',
        errorTitle: 'An error occurred',
        errorText: 'An error occurred during refreshing API access. Please reload the page.',
        successAction: (data) => {
          window.localStorage.setItem('token', data.access_token);
          this.$root.userLoggedIn = true;
          window.localStorage.setItem('last-token-refresh', +new Date());
          Vue.prototype.$http.defaults.headers.Authorization = 'Bearer ' + SanitizeUtils.getToken();
        }
      });
    },
    autoRefreshToken () {
      setTimeout(() => {
        if (window.localStorage.getItem('token') && this.$root.userLoggedIn) {
          let lastRefresh = parseInt(window.localStorage.getItem('last-token-refresh'), 10);

          if (+new Date() - lastRefresh > 30 * 60 * 1000) {
            this.refreshToken();
          }
        }

        this.autoRefreshToken();
      }, 1 * 60 * 1000);
    },
    loadGoogleMapsAPI () {
      if (!document.querySelector('#google-maps-api-embed')) {
        let script = document.createElement('script');
        script.type = 'text/javascript';
        script.id = 'google-maps-api-embed';
        script.src = 'https://maps.googleapis.com/maps/api/js?key=' + this.$googleMapsApiKey + '&libraries=places';
        document.body.appendChild(script);
      }
    },
    checkActivity () {
      if (this.$root.userLoggedIn) {
        let timeDiff = +new Date() - this.$root.lastClick;

        if (timeDiff > 30 * 60 * 1000) {
          this.$router.push('/logout');
        } else if (timeDiff > 27 * 60 * 1000) {
          this.$root.showInactivityAlert = true;
        } else {
          this.$root.showInactivityAlert = false;
        }
      }

      setTimeout(() => {
        this.checkActivity();
      }, 15 * 1000);
    },
    recordActivity () {
      this.$root.lastClick = +new Date();
    }
  }
};
</script>

<style lang="scss">
@import './assets/scss/custom-elements.scss';
@import './assets/scss/vue-wysiwyg.scss';

:root {
  --light-bg: #fff;
}

body.no-scroll {
  overflow: hidden;
}

.switch-enter-to,
.switch-leave-to {
  transition: opacity .25s ease-out;
}

.switch-enter,
.switch-leave-to {
  left: 0;
  opacity: 0;
  position: absolute;
  top: 0;
  transform: none;
  width: 100%;
}

.airports-list-overlay {
  align-items: center;
  background: rgba(255, 255, 255, .85);
  display: flex;
  height: 100%;
  justify-content: center;
  left: 0;
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 10000;
}

.inactivity-alert {
  align-items: center;
  background: var(--danger);
  box-shadow: 0 0 10px rgba(0, 0, 0, .125);
  color: #fff;
  display: flex;
  font-size: 20px;
  height: 48px;
  justify-content: center;
  left: 0;
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 100000000;
}
</style>
